import { createContext, useContext } from "react";
import { Link, Outlet, getRouteApi } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Membership } from "@ortura/shared";
import { useApi } from "../api/api-context";
import { findMembership, meQuery } from "../api/platform";
import { useAuth } from "../auth/auth-context";
import { CenteredPage } from "../components/centered-page";
import { Wordmark } from "../components/wordmark";
import { Select } from "../components/ui/form";
import { changeLanguage, LANGUAGES, type Language } from "../i18n";

const route = getRouteApi("/_authed/t/$tenant");

// Aktiver Mandant für alle Seiten unterhalb von /t/{subdomain}.
const TenantContext = createContext<Membership | null>(null);

export function useTenant(): Membership {
  const tenant = useContext(TenantContext);
  if (!tenant) throw new Error("useTenant() außerhalb des Mandantenbereichs verwendet");
  return tenant;
}

// Rahmen des Mandantenbereichs: dunkle Navigation links (CI), Inhalt rechts.
// Die Subdomain aus der Adresse wird gegen die eigenen Mitgliedschaften
// aufgelöst (Entscheidung 35). Autoritativ prüft trotzdem die API jede Anfrage
// (Header X-Ortura-Tenant gegen memberships, Entscheidung 29).
export function TenantLayout() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const auth = useAuth();
  const { tenant: subdomain } = route.useParams();
  const { data: me } = useSuspenseQuery(meQuery(api));
  const membership = findMembership(me.memberships, subdomain);

  if (!membership) {
    return (
      <CenteredPage account>
        <div className="flex flex-col gap-3">
          <h1 className="text-lg text-ink">{t("tenant.notMember")}</h1>
          <p className="text-sm text-ink-muted">{t("tenant.notMemberHint")}</p>
          <Link to="/select" className="text-sm text-link-fg hover:underline">
            {t("tenant.switch")}
          </Link>
        </div>
      </CenteredPage>
    );
  }

  const navLink =
    "rounded-md px-3 py-2 text-sm font-medium text-nebel hover:bg-tiefsee hover:text-papier";
  const email = auth.state.status === "authenticated" ? auth.state.user.email : undefined;

  return (
    <TenantContext.Provider value={membership}>
      <div className="flex min-h-screen flex-col md:flex-row">
        <nav className="flex shrink-0 flex-col gap-6 bg-nacht p-5 md:w-60">
          <Wordmark tone="negativ" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-papier">{membership.name}</span>
            <span className="text-xs text-nebel">{t(`tenant.status.${membership.status}`)}</span>
          </div>
          <div className="flex flex-col gap-1">
            {/* activeOptions.exact: "Übersicht" nur auf genau /t/{subdomain} aktiv */}
            <Link
              to="/t/$tenant"
              params={{ tenant: subdomain }}
              activeOptions={{ exact: true }}
              className={navLink}
              activeProps={{ className: "bg-tiefsee text-papier" }}
            >
              {t("nav.overview")}
            </Link>
            <Link
              to="/t/$tenant/members"
              params={{ tenant: subdomain }}
              className={navLink}
              activeProps={{ className: "bg-tiefsee text-papier" }}
            >
              {t("nav.members")}
            </Link>
          </div>

          <div className="mt-auto flex flex-col gap-3 text-xs text-nebel">
            {me.memberships.length > 1 && (
              <Link to="/select" className="hover:text-papier">
                {t("tenant.switch")}
              </Link>
            )}
            <label className="flex flex-col gap-1">
              {t("nav.language")}
              <Select
                className="h-8 border-tiefsee bg-tiefsee text-xs text-papier"
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value as Language)}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {t(`languages.${lang}`)}
                  </option>
                ))}
              </Select>
            </label>
            {/* Konto und Abmelden als deutlich sichtbarer Block am Fuß der Navigation */}
            <div className="flex flex-col gap-2 border-t border-tiefsee pt-4">
              {email && <span className="truncate text-sm text-papier">{email}</span>}
              <button
                type="button"
                className="h-9 rounded-md border border-schiefer px-3 text-sm font-medium text-papier transition-colors hover:border-tiefsee hover:bg-tiefsee"
                onClick={() => void auth.logout()}
              >
                {t("nav.logout")}
              </button>
            </div>
          </div>
        </nav>

        <main className="flex-1 bg-page p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </TenantContext.Provider>
  );
}
